import { createFileRoute } from "@tanstack/react-router";
import { Building2, Loader2, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { AccountCreateModal } from "@/components/accounts/AccountCreateModal";
import { AccountDrawer } from "@/components/accounts/AccountDrawer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAccountsList } from "@/lib/hooks/accounts";
import { useCurrentUser } from "@/lib/hooks/auth";
import type { AccountRead } from "@/lib/schemas/accounts";
import { isReadOnlyRole, type UserRole } from "@/lib/schemas/users";

// Global-search (ADR-0105) deep-links here as `/accounts?highlight=<id>` →
// auto-open that account's drawer once the list has loaded.
export const Route = createFileRoute("/_app/accounts")({
  validateSearch: z.object({ highlight: z.guid().optional() }).parse,
  component: AccountsPage,
});

function AccountsPage(): JSX.Element {
  const { t } = useTranslation();
  const accounts = useAccountsList();
  const me = useCurrentUser();
  const { highlight } = Route.useSearch();
  const readOnly = isReadOnlyRole((me.data?.role ?? "peer_read") as UserRole);
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<AccountRead | null>(null);

  useEffect(() => {
    if (!highlight || !accounts.data) return;
    const match = accounts.data.items.find((a) => a.id === highlight);
    if (match) setSelected(match);
  }, [highlight, accounts.data]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Building2 className="h-6 w-6" />
          {t("accounts.title")}
        </h1>
        {!readOnly && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />
            {t("accounts.create.title")}
          </Button>
        )}
      </div>

      {accounts.isLoading ? (
        <Loader2 className="h-6 w-6 animate-spin" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("accounts.field.name")}</TableHead>
              <TableHead>{t("accounts.field.type")}</TableHead>
              <TableHead>{t("accounts.field.status")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(accounts.data?.items ?? []).map((a) => (
              <TableRow key={a.id} className="cursor-pointer" onClick={() => setSelected(a)}>
                <TableCell className="font-medium">{a.name}</TableCell>
                <TableCell>
                  <Badge variant="outline">{t(`accounts.type.${a.account_type}`)}</Badge>
                </TableCell>
                <TableCell>{a.status}</TableCell>
              </TableRow>
            ))}
            {(accounts.data?.items ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground">
                  {t("accounts.empty")}
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      )}

      <AccountCreateModal open={createOpen} onOpenChange={setCreateOpen} />
      <AccountDrawer account={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
